import { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import { Copy, Check } from 'lucide-react';

import { Button } from '@/components/ui/button';

export type PropertyHandoverQrProps = {
  /** The exact string encoded in QR (ex: universalLink) */
  payload: string;

  /** Pixel size for the SVG */
  size?: number;

  /** Optional header text */
  title?: string;

  /** Optional helper text */
  subtitle?: string;

  /** Show payload string under QR (useful for debugging/support) */
  showPayloadText?: boolean;

  /** Copy button copies this; defaults to payload */
  copyValue?: string;

  className?: string;
};

export function PropertyHandoverQr({
  payload,
  size = 220,
  title = 'Scan to claim ownership',
  subtitle = 'Open HomeVein on your phone and scan this QR code.',
  showPayloadText = false,
  copyValue,
  className,
}: PropertyHandoverQrProps) {
  const [svg, setSvg] = useState<string>('');
  const [copied, setCopied] = useState(false);

  const valueToCopy = useMemo(() => copyValue ?? payload, [copyValue, payload]);

  useEffect(() => {
    let cancelled = false;

    async function gen() {
      if (!payload) {
        setSvg('');
        return;
      }

      // SVG looks sharp at any size and is easy to style
      const out = await QRCode.toString(payload, {
        type: 'svg',
        margin: 1,
        errorCorrectionLevel: 'M',
        // width isn't always respected the same way for SVG; we set via wrapper too
        width: size,
        color: {
          dark: '#000000',
          light: '#FFFFFF',
        },
      });

      if (!cancelled) setSvg(out);
    }

    gen().catch(() => {
      if (!cancelled) setSvg('');
    });

    return () => {
      cancelled = true;
    };
  }, [payload, size]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(valueToCopy);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      setCopied(false);
    }
  };

  if (!payload) {
    return (
      <div className={className}>
        <div className="text-muted-foreground text-sm">No QR payload available.</div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="flex flex-col items-center gap-3">
        {(title || subtitle) && (
          <div className="space-y-1 text-center">
            {title ? <div className="text-sm font-medium">{title}</div> : null}
            {subtitle ? <div className="text-muted-foreground text-xs">{subtitle}</div> : null}
          </div>
        )}

        {/* QR */}
        <div className="rounded-xl border bg-white p-3 shadow-sm" style={{ width: size + 24, maxWidth: '100%' }}>
          {/* Render SVG safely. We control generation, so this is OK. */}
          <div style={{ width: size, height: size }} className="mx-auto" dangerouslySetInnerHTML={{ __html: svg }} />
        </div>

        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={handleCopy} className="gap-2">
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? 'Copied' : 'Copy link'}
          </Button>
        </div>

        {showPayloadText ? (
          <div className="bg-muted/40 max-w-[480px] rounded-md p-2 font-mono text-[11px] break-all">{payload}</div>
        ) : null}
      </div>
    </div>
  );
}
